import { PlusCircleFilled } from '@ant-design/icons';
import type { OffsetOptions, Placement } from '@floating-ui/react';
import type { FC, MouseEventHandler } from 'react';
import { memo, useCallback, useState } from 'react';
import type { NodeEnum, OnSelectBlock } from '../../types';
import {
  PortalToFollowElem,
  PortalToFollowElemContent,
  PortalToFollowElemTrigger,
} from '../portal-to-follow-elem';
import List from './list';

type BlockSelectorProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSelect: OnSelectBlock;
  trigger?: (open: boolean) => React.ReactNode;
  placement?: Placement;
  offset?: OffsetOptions;
  triggerStyle?: React.CSSProperties;
  triggerClassName?: (open: boolean) => string;
  triggerInnerClassName?: string;
  popupClassName?: string;
  asChild?: boolean;
  availableBlocksTypes?: NodeEnum[];
  disabled?: boolean;
  noBlocks?: boolean;
};
const BlockSelector: FC<BlockSelectorProps> = ({
  open: openFromProps,
  onOpenChange,
  onSelect,
  trigger,
  placement = 'right',
  offset = 6,
  triggerClassName,
  triggerInnerClassName,
  triggerStyle,
  popupClassName,
  asChild,
  availableBlocksTypes,
  disabled,
}) => {
  const [localOpen, setLocalOpen] = useState(false);
  const open = openFromProps ?? localOpen;
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setLocalOpen(newOpen);

      if (onOpenChange) onOpenChange(newOpen);
    },
    [onOpenChange],
  );
  const handleTrigger = useCallback<MouseEventHandler<HTMLDivElement>>(
    (e) => {
      if (disabled) return;
      e.stopPropagation();
      handleOpenChange(!open);
    },
    [handleOpenChange, open, disabled],
  );
  const handleSelect = useCallback<OnSelectBlock>(
    (type) => {
      handleOpenChange(false);
      onSelect(type);
    },
    [handleOpenChange, onSelect],
  );

  return (
    <PortalToFollowElem
      placement={placement}
      offset={offset}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <PortalToFollowElemTrigger
        asChild={asChild}
        onClick={handleTrigger}
        className={triggerInnerClassName}
      >
        {trigger ? (
          trigger(open)
        ) : (
          <div
            className={`
                  flex items-center justify-center
                  w-4 h-4 rounded-full bg-white cursor-pointer z-10
                  ${triggerClassName?.(open)}
                `}
            style={triggerStyle}
          >
            <PlusCircleFilled className="text-[18px] text-blue-500" />
          </div>
        )}
      </PortalToFollowElemTrigger>
      <PortalToFollowElemContent className="z-[1000]">
        <div
          className={`w-[200px] px-[14px] py-[12px] space-y-[14px]  rounded-md border-[0.5px] border-gray-200 bg-white shadow-lg ${popupClassName}`}
        >
          <div className="text-sm text-[#777A80]">选择节点</div>
          <div
            className="w-full flex items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <List
              onSelect={handleSelect}
              availableBlocksTypes={availableBlocksTypes}
            />
          </div>
        </div>
      </PortalToFollowElemContent>
    </PortalToFollowElem>
  );
};

export default memo(BlockSelector);
